/**
 * App Data Context - Initializes and manages the centralized data cache
 * Loads all app data on mount and provides initialization status
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { appDataStore } from '@/services/cache/appDataStore';
import LoadingSpinner from '@/components/common/LoadingSpinner';

interface AppDataContextType {
  isInitialized: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

export function AppDataProvider({ children }: { children: React.ReactNode }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    initializeAppData();
  }, []);

  // When a background refresh updates the song or album list, re-run the
  // relevant queries so the UI reflects the latest data without a blocking
  // reload, and surface the in-flight refresh state.
  useEffect(() => {
    return appDataStore.subscribe(() => {
      setIsRefreshing(appDataStore.isRefreshing());
      queryClient.invalidateQueries({ queryKey: ['songs'] });
      queryClient.invalidateQueries({ queryKey: ['albums'] });
    });
  }, [queryClient]);

  async function initializeAppData() {
    try {
      setIsLoading(true);
      setError(null);
      console.log('[AppDataProvider] Initializing app data...');
      await appDataStore.initialize();
      console.log('[AppDataProvider] App data initialized successfully');
      setIsInitialized(true);
    } catch (err) {
      console.error('[AppDataProvider] Failed to initialize app data:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }

  async function refresh() {
    await appDataStore.refresh();
  }

  // Show loading spinner while initializing
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show error if initialization failed
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center text-white">
          <h2 className="text-xl font-bold mb-2">Failed to load app data</h2>
          <p className="text-text-secondary mb-4">{error.message}</p>
          <button onClick={initializeAppData} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <AppDataContext.Provider value={{ isInitialized, isLoading, isRefreshing, error, refresh }}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error('useAppData must be used within AppDataProvider');
  }
  return context;
}
